/**
 * Centralized Storage Service
 *
 * A type-safe, enum-based storage layer that wraps localStorage operations.
 * Provides automatic JSON serialization/deserialization, error handling,
 * and cleanup capabilities.
 *
 * @example
 * ```typescript
 * // Import the service
 * import StorageService from '@/utils/storage'
 * import { StorageKey } from '@/types/storage'
 *
 * // Store user data
 * const user = { id: '123', name: 'John' }
 * StorageService.set(StorageKey.USER, user)
 *
 * // Retrieve user data with type safety
 * const savedUser = StorageService.get<User>(StorageKey.USER)
 *
 * // Get with default fallback
 * const profile = StorageService.getOrDefault(StorageKey.PROFILE, {})
 *
 * // Check existence
 * if (StorageService.has(StorageKey.USER)) {
 *   // User is logged in
 * }
 *
 * // Remove specific item
 * StorageService.remove(StorageKey.USER)
 *
 * // Clear all app storage (e.g., on logout)
 * StorageService.clearAll()
 * ```
 */

import { StorageKey, StorageData } from '@/types/storage';
import { EnvVarKey } from '@/config/keys';
import { logger } from '@/utils/logger';

/**
 * StorageService class providing type-safe localStorage operations.
 * Implemented as a singleton to ensure consistent state across the app.
 */
class StorageService {
  private static instance: StorageService;
  private readonly isDevelopment: boolean;

  /**
   * Private constructor to enforce singleton pattern.
   */
  private constructor() {
    this.isDevelopment = import.meta.env[EnvVarKey.DEV];
  }

  /**
   * Get the singleton instance of StorageService.
   */
  public static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }

  /**
   * Retrieves a value from localStorage with automatic JSON parsing.
   *
   * @template T - The expected type of the stored value
   * @param key - The storage key enum value
   * @returns The parsed value of type T, or null if not found or parse error
   *
   * @example
   * ```typescript
   * const user = StorageService.get<AuthUser>(StorageKey.USER)
   * if (user) {
   *   console.log(user.username)
   * }
   * ```
   */
  public get<T = StorageData>(key: StorageKey): T | null {
    try {
      const item = localStorage.getItem(key);

      if (item === null) {
        return null;
      }

      // Try to parse as JSON
      try {
        return JSON.parse(item) as T;
      } catch {
        // If parsing fails, return as-is (for plain strings)
        return item as T;
      }
    } catch (error) {
      logger.storage.warn(
        `[StorageService] Failed to get item with key "${key}": ${error}`
      );
      return null;
    }
  }

  /**
   * Stores a value in localStorage with automatic JSON serialization.
   *
   * @template T - The type of value being stored
   * @param key - The storage key enum value
   * @param value - The value to store (will be JSON stringified)
   *
   * @example
   * ```typescript
   * StorageService.set(StorageKey.USER, { id: '123', name: 'John' })
   * StorageService.set(StorageKey.PROFILE, { theme: 'dark', sizing: 'md' })
   * ```
   */
  public set<T = StorageData>(key: StorageKey, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);

      if (this.isDevelopment) {
        logger.storage.debug(`[StorageService] Stored item with key "${key}"`);
      }
    } catch (error) {
      logger.storage.warn(
        `[StorageService] Failed to set item with key "${key}": ${error}`
      );
    }
  }

  /**
   * Removes a specific item from localStorage.
   *
   * @param key - The storage key enum value
   *
   * @example
   * ```typescript
   * StorageService.remove(StorageKey.USER)
   * ```
   */
  public remove(key: StorageKey): void {
    try {
      localStorage.removeItem(key);

      if (this.isDevelopment) {
        logger.storage.debug(`[StorageService] Removed item with key "${key}"`);
      }
    } catch (error) {
      logger.storage.warn(
        `[StorageService] Failed to remove item with key "${key}": ${error}`
      );
    }
  }

  /**
   * Clears all application storage by removing ALL localStorage entries.
   * This ensures a complete clean slate on logout for data privacy.
   *
   * @example
   * ```typescript
   * // On user logout
   * StorageService.clearAll()
   * ```
   */
  public clearAll(): void {
    try {
      // Clear ALL localStorage items to ensure complete clean slate
      localStorage.clear();
      // Clear sessionStorage too — it survives page reloads, so without this
      // the next user would inherit the previous user's active tab, selected
      // group ID, velocity filters, etc.
      sessionStorage.clear();

      if (this.isDevelopment) {
        logger.storage.debug('[StorageService] Cleared all storage');
      }
    } catch (error) {
      logger.storage.warn(
        `[StorageService] Failed to clear all storage: ${error}`
      );
    }
  }

  /**
   * Checks if a value exists in localStorage.
   *
   * @param key - The storage key enum value
   * @returns true if the key exists, false otherwise
   *
   * @example
   * ```typescript
   * if (StorageService.has(StorageKey.USER)) {
   *   console.log('User is logged in')
   * }
   * ```
   */
  public has(key: StorageKey): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      logger.storage.warn(
        `[StorageService] Failed to check existence of key "${key}": ${error}`
      );
      return false;
    }
  }

  /**
   * Retrieves a value from storage, returning a default value if not found.
   *
   * @template T - The expected type of the stored value
   * @param key - The storage key enum value
   * @param defaultValue - The value to return if the key doesn't exist
   * @returns The stored value or the default value
   *
   * @example
   * ```typescript
   * const profile = StorageService.getOrDefault(StorageKey.PROFILE, { theme: 'light' })
   * ```
   */
  public getOrDefault<T = StorageData>(key: StorageKey, defaultValue: T): T {
    const value = this.get<T>(key);
    return value !== null ? value : defaultValue;
  }

  /**
   * Returns all storage keys defined in the StorageKey enum.
   *
   * @returns Array of all StorageKey values
   *
   * @example
   * ```typescript
   * const allKeys = StorageService.keys()
   * console.log('All storage keys:', allKeys)
   * ```
   */
  public keys(): StorageKey[] {
    return Object.values(StorageKey);
  }
}

// Export singleton instance as default
export default StorageService.getInstance();
